// @flow

import React, { Component, useEffect } from 'react';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';

import SignIn from 'views/authentication/sign_in/Index';
import SignUp from 'views/authentication/sign_up/Index';
import PrivateRoute from 'utils/Auth';
import PageNotFound from 'components/PageNotFound';
import { AuthGate, Admin, AdminUser } from 'auth';

const LeadsOfflineOwners = React.lazy(() =>
  import('modules/admin/views/LeadsOfflineOwners')
);
const HomeFormScreen = React.lazy(() =>
  import('modules/external_tenant/components/homes/HomeFormScreen')
);
const Home = React.lazy(() => import('views/homes/show/Home'));
const PreviewHome = React.lazy(() => import('views/homes/show/PreviewHome'));
const Favorite = React.lazy(() => import('views/homes/favorite/Favorite'));
const Appointment = React.lazy(() => import('views/homes/show/Appointment'));
const RelatedDepasFeedback = React.lazy(() =>
  import('views/appointments/RelatedDepasFeedback')
);
const WeWantHelpYouSurvey = React.lazy(() =>
  import('views/appointments/WeWantHelpYouSurvey')
);
const VisitedDepas = React.lazy(() =>
  import('views/appointments/VisitedDepas')
);
const WelcomeHomie = React.lazy(() =>
  import('views/authentication/WelcomeHomie')
);
const RenterApplication = React.lazy(() =>
  import('views/renter_application/RenterApplicationComponent')
);
const SuccessPayment = React.lazy(() =>
  import('views/renter_application/SuccessPaymentComponent')
);
const ApplicationsList = React.lazy(() =>
  import('views/renter_application/ApplicationsList')
);
const BrowserNotSupported = React.lazy(() =>
  import('views/browser_not_supported/BrowserNotSupported')
);
const ResetPassword = React.lazy(() =>
  import('views/authentication/ResetPassword')
);
const EditAccount = React.lazy(() => import('views/account/EditAccount'));
const RenewRenterContract = React.lazy(() =>
  import('views/renewal/RenewRenterContract')
);
const ApplicationSectionPayment = React.lazy(() =>
  import('views/renter_application/ApplicationSectionPayment')
);
const Onboarding = React.lazy(() => import('views/onboarding/ProcessSteps'));
const OnboardingCompleteView = React.lazy(() =>
  import('views/onboarding/OnboardingCompleteView')
);
const EditOnboarding = React.lazy(() =>
  import('views/onboarding/EditOnboarding')
);
const OwnerDepa = React.lazy(() =>
  import('views/homes/owner_my_depa/OwnerDepa')
);
const OwnerDepaSingle = React.lazy(() =>
  import('views/homes/owner_my_depa/OwnerDepaSingle')
);
const HowToWorkHomie = React.lazy(() => import('views/HowToWorkHomie'));
const ContinueContractSignature = React.lazy(() =>
  import('views/ContinueContractSignature')
);
const VerifyLogRedirect = React.lazy(() => import('views/VerifyLogRedirect'));
const HomieLegalContent = React.lazy(() =>
  import('views/legal/HomieLegalContent')
);
const H360LegalContent = React.lazy(() =>
  import('views/legal/H360LegalContent')
);
const OwnerAutologin = React.lazy(() =>
  import('modules/admin/views/OwnerAutologin')
);
const FirstPaymentCreator = React.lazy(() =>
  import('views/FirstPaymentCreator')
);
const RepairmentReportResume = React.lazy(() =>
  import('components/homes/ownership/RepairmentReportResume')
);
const MeetingRepairmentReport = React.lazy(() =>
  import('components/homes/ownership/MeetingRepairmentReportComponent')
);

const AdminDashboardFix = React.lazy(() =>
  import('views/dashboard/iguanafix/DashboardView')
);
const ReportView = React.lazy(() =>
  import('views/dashboard/iguanafix/ReportView')
);

const ViewDeposit = React.lazy(() =>
  import('react/modules/navent/PageDeposit')
);

/*const PricingTool = React.lazy(() =>
  import('react/modules/pricing_tool/Container')
);*/

const PricingTool = function() {
  useEffect(() => {
    window.location.href = 'https://homie.mx/propietario/calculadora';
  }, []);
  return null;
};

const Financing = React.lazy(() => import('modules/admin/views/Financing'));

const HotSale = React.lazy(() => import('react/modules/hot_sale/Container'));

const Index = React.lazy(() => import('views/index/Index'));

const HomesIndex = React.lazy(() => import('views/homes/index/IndexView'));

const ApplicationRedirect = React.lazy(() =>
  import('components/homes/redirection/ApplicationRedirect')
);

const AppointmentRedirect = React.lazy(() =>
  import('components/homes/redirection/AppointmentRedirect')
);

const UsuarioRouter = ({ match }: any) => (
  <Switch>
    <PrivateRoute
      exact
      path={match.path + '/bienvenido'}
      component={WelcomeHomie}
    />
    <PrivateRoute
      exact
      path={match.path + '/cambiar-password'}
      component={ResetPassword}
    />
    <PrivateRoute
      exact
      path={match.path + '/cuenta/editar'}
      component={EditAccount}
    />
    <PrivateRoute
      exact
      path={match.path + '/aplicaciones/:home_app_id/editar/:step?'}
      component={RenterApplication}
    />
    <Redirect exact from={match.path} to={`${match.path}/aplicaciones`} />
    <Route component={PageNotFound} />
  </Switch>
);

const InquilinoRouter = ({ match }: any) => (
  <Switch>
    <PrivateRoute
      exact
      path="/inquilino/:home_id/aplicacion-completada"
      component={SuccessPayment}
    />
    <PrivateRoute
      exact
      path="/inquilino/aplicaciones/:home_app_id/editar/:step?"
      component={RenterApplication}
    />
    <PrivateRoute
      exact
      path="/inquilino/aplicaciones/:home_app_id/pagar"
      component={ApplicationSectionPayment}
    />
    <PrivateRoute
      exact
      path="/inquilino/inmuebles/:home_id/citas/:home_meeting_id/seguir-cita"
      component={WeWantHelpYouSurvey}
    />
    <PrivateRoute
      exact
      path="/inquilino/inmuebles/:home_id/aplicar"
      component={RenterApplication}
    />
    <PrivateRoute
      exact
      path="/inquilino/inmuebles/:home_id/similares"
      component={RelatedDepasFeedback}
    />
    <PrivateRoute
      exact
      path="/inquilino/rentas/:rent_id/:tab/:subTab/:breakdownId/citas"
      component={RenewRenterContract}
    />
    <PrivateRoute
      exact
      path="/inquilino/rentas/:rent_id/:tab/:subTab?/:breakdownId?"
      component={RenewRenterContract}
    />
    <PrivateRoute
      exact
      path="/inquilino/:tab/rentas/:subTab?/:entityId?"
      component={RenewRenterContract}
    />
    <PrivateRoute exact path="/inquilino/citas" component={Appointment} />
    <PrivateRoute
      exact
      path="/inquilino/inmuebles-visitados"
      component={VisitedDepas}
    />
    <PrivateRoute
      exact
      path="/inquilino/crear-primer-pago/:rent_id/:type"
      component={FirstPaymentCreator}
    />
    <PrivateRoute
      exact
      path={match.path + '/aplicaciones'}
      component={ApplicationsList}
    />
    <Redirect exact path={match.path + '/nodeposito'} to={`/`} />
    <Redirect exact from={match.path} to={`${match.path}/pagos/rentas`} />
    <Route component={PageNotFound} />
  </Switch>
);

const PropietarioRouter = ({ match }: any) => (
  <Switch>
    <PrivateRoute
      exact
      path="/propietario/registra-tu-inmueble"
      component={Onboarding}
    />
    <PrivateRoute
      exact
      path="/propietario/publicar/inmueble/:home_id/completado"
      component={OnboardingCompleteView}
    />
    <PrivateRoute
      exact
      path="/propietario/publicar/inmueble/editar/:home_id/:step?"
      component={Onboarding}
    />
    <Redirect
      exact
      from="/propietario/publicar/inmueble"
      to="/propietario/registra-tu-inmueble"
    />
    <PrivateRoute
      exact
      path="/propietario/inmuebles/:id/vista-previa"
      component={PreviewHome}
    />
    <PrivateRoute
      exact
      path="/propietario/inmuebles/:home_id/citas"
      component={OwnerDepa}
      propsComponent={{ tab: 'citas', child: 0 }}
    />
    <PrivateRoute
      exact
      path="/propietario/inmuebles/:home_id/disponibilidad"
      component={OwnerDepa}
      propsComponent={{ tab: 'citas', child: 1 }}
    />
    <PrivateRoute
      exact
      path="/propietario/inmuebles/:home_id/detalles"
      component={OwnerDepaSingle}
    />
    <PrivateRoute
      exact
      path="/propietario/inmuebles/:home_id/editar/:tab?"
      component={EditOnboarding}
    />
    <PrivateRoute
      exact
      path="/propietario/inmuebles/:home_id/editar/:tab/section"
      component={EditOnboarding}
      propsComponent={{ editionSection: true }}
    />
    <PrivateRoute
      exact
      path="/propietario/:tab/:home_id?/:subTab?"
      component={OwnerDepa}
    />
    <PrivateRoute
      exact
      path="/propietario/renta/:rent_id/averias/reporte/:report_id"
      component={() => <RepairmentReportResume isFault />}
    />

    <PrivateRoute
      exact
      path="/propietario/renta/:rent_id/vicios-ocultos/reporte/:report_id"
      component={RepairmentReportResume}
    />
    <PrivateRoute
      exact
      path="/propietario/renta/:rent_id/:tab/reporte/:report_id/confirmacion-cita"
      component={MeetingRepairmentReport}
    />
    <Redirect exact from="/propietario" to="/propietario/inmuebles" />
    <Route component={PageNotFound} />
  </Switch>
);

const AdminRouter = ({ match }: any) => (
  <Switch>
    <Route
      exact
      path={match.path + '/iguana-fix'}
      component={AdminDashboardFix}
    />
    <Route
      exact
      path={match.path + '/iguana-fix/reporte/:report_id'}
      component={ReportView}
    />
    <Route component={PageNotFound} />
  </Switch>
);

const HomieNew = ({ component }: any) => {
  if (process.env.ENV !== 'development') {
    window.location.href = 'https://homie.mx/new';
    return;
  }

  return component;
};

type PropsType = {
  agency: any
};
class Routes extends Component<PropsType> {
  render() {
    return (
      <Switch>
        <Route
          exact
          path="/propietario/calculadorahomie"
          component={PricingTool}
        />
        <Route
          exact
          path="/"
          render={() => <HomieNew component={<Index />} />}
        />
        <Route exact path="/legal" component={HomieLegalContent} />
        <Route exact path="/legal/:tab?" component={HomieLegalContent} />
        <Route exact path="/360/legal/:tab?" component={H360LegalContent} />
        <PrivateRoute
          exact
          path="/agencia/:tab/:slug"
          component={OwnerDepa}
          propsComponent={{ tab: 'citas', child: 0, agency: this.props.agency }}
        />
        <Route
          exact
          path="/inmuebles"
          render={() => <HomieNew component={<HomesIndex />} />}
        />

        <PrivateRoute
          exact
          path="/inmuebles/:id/aplicacion"
          component={ApplicationRedirect}
        />
        <PrivateRoute
          exact
          path="/inmuebles/:id/cita/:date/:time"
          component={AppointmentRedirect}
        />

        <Route exact path="/inmuebles/:slug/info" component={HomeFormScreen} />
        <Route exact path="/inmuebles/:slug/:tab?" component={Home} />

        <Route exact path="/favoritos" component={Favorite} />
        <Route
          exact
          path="/autenticacion/propietario/:userId"
          component={OwnerAutologin}
        />
        <Route exact path="/autenticacion" component={SignIn} />
        <Route exact path="/como-funciona-homie" component={HowToWorkHomie} />
        <Route
          exact
          path="/navegador-no-compatible"
          component={BrowserNotSupported}
        />
        <Route
          exact
          path="/registro/inquilino"
          render={props => <SignUp {...props} status={true} />}
        />
        <Route
          exact
          path="/registro/propietario"
          render={props => <SignUp {...props} status={false} />}
        />
        <Route
          exact
          path="/leads_offline_propietario"
          render={props => (
            <AuthGate allowed={[Admin]}>
              <LeadsOfflineOwners {...props} />
            </AuthGate>
          )}
        />
        <Route
          exact
          path="/financiamiento"
          render={props => (
            <AuthGate allowed={[Admin]}>
              <Financing {...props} />
            </AuthGate>
          )}
        />
        <Route
          exact
          path="/navegador-no-compatible"
          component={BrowserNotSupported}
        />
        <Route exact path="/como-funciona-homie" component={HowToWorkHomie} />
        <Route
          exact
          path="/restaurar-password/:reset_password_token"
          component={() => <ResetPassword isRestorePass={true} />}
        />
        <PrivateRoute
          exact
          path="/agencia/:slug/inmuebles/:id/vista-previa"
          component={PreviewHome}
        />
        <PrivateRoute
          exact
          path="/agencia/:slug/inmuebles/:home_id/detalles"
          component={OwnerDepaSingle}
          propsComponent={{ agency: this.props.agency }}
        />
        <PrivateRoute
          exact
          path="/agencia/:slug/publicar/inmueble/nuevo"
          component={Onboarding}
          propsComponent={{ agency: this.props.agency }}
        />
        <PrivateRoute
          exact
          path="/agencia/:slug/publicar/inmueble/editar/:home_id/:step?"
          component={Onboarding}
          propsComponent={{ agency: this.props.agency }}
        />
        <PrivateRoute
          exact
          path="/agencia/inmuebles"
          component={OwnerDepa}
          propsComponent={{ tab: 'inmuebles', child: 0 }}
        />
        <Route path="/usuario" component={UsuarioRouter} />
        <Route path="/inquilino" component={InquilinoRouter} />
        <Route path="/propietario" component={PropietarioRouter} />
        <Route
          path="/admin"
          render={props => (
            <AuthGate allowed={[AdminUser]}>
              <AdminRouter {...props} />
            </AuthGate>
          )}
        />
        <Route exact path="/hotsale" component={HotSale} />
        <Route
          exact
          path="/propietario/calculadorahomie"
          component={PricingTool}
        />
        <PrivateRoute
          exact
          path="/correo-confirmado"
          component={WelcomeHomie}
        />
        <PrivateRoute
          exact
          path="/correo-error-confirmacion"
          component={WelcomeHomie}
        />
        <PrivateRoute
          exact
          path="/continuar-firma-contrato/:id?"
          component={ContinueContractSignature}
        />
        <PrivateRoute exact path="/autenticar" component={VerifyLogRedirect} />
        <PrivateRoute
          exact
          path="/admin/inmuebles/:home_id/editar/:step"
          component={props => (
            <Redirect
              to={`/propietario/inmuebles/${props.match.params.home_id}/editar/${props.match.params.step}`}
            />
          )}
        />
        <PrivateRoute path="/deposito/msi" component={ViewDeposit} />
        <Route component={PageNotFound} />
      </Switch>
    );
  }
}

export default withRouter(Routes);
